import { useId, useState } from "react";

import { PROVENANCE_COPY } from "./provenance.constants.ts";

/**
 * Naming an owner who is on ONDO.
 *
 * There is no autocomplete here and there cannot be one. `principals_read` is
 * `acts_as(id)`, so the platform will not tell one account whether another
 * exists — a field that offered "did you mean Erwin Wurm?" would be the widest
 * directory probe on the platform, and the server gives the same refusal for
 * "no such id" and "not permitted" precisely so this cannot be built.
 *
 * The green line therefore only ever repeats something the author already knew.
 */
export function AttachOndoId({
  attached,
  onAttach,
  onDetach,
}: {
  attached: { ondoId: string; note: string } | null;
  onAttach: (raw: string) => string | null;
  onDetach: () => void;
}) {
  const field = useId();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  if (attached) {
    return (
      <p className="provenance__ondo">
        <span aria-hidden="true">●</span> {attached.note}
        {attached.ondoId !== "" && <span className="provenance__ondo-id">{attached.ondoId}</span>}
        <button type="button" className="provenance__link" onClick={onDetach}>
          {PROVENANCE_COPY.detach}
        </button>
      </p>
    );
  }

  if (!open) {
    return (
      <button type="button" className="provenance__link" onClick={() => setOpen(true)}>
        {PROVENANCE_COPY.attachOndoId}
      </button>
    );
  }

  return (
    <div className="provenance__attach">
      <label htmlFor={field}>ONDO id</label>
      <div className="provenance__attach-row">
        <input
          id={field}
          value={value}
          placeholder="ONDO-1042"
          onChange={(event) => setValue(event.target.value)}
        />
        <button
          type="button"
          onClick={() => {
            const failed = onAttach(value);
            setProblem(failed);
            if (!failed) {
              setValue("");
              setOpen(false);
            }
          }}
        >
          {PROVENANCE_COPY.attachAction}
        </button>
      </div>
      {problem !== null && (
        <p className="provenance__problem" role="alert">
          {problem}
        </p>
      )}
      <p className="provenance__help">{PROVENANCE_COPY.attachHelp}</p>
    </div>
  );
}
